import { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const ProjectStatus = () => {
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [projects, setProjects] = useState(null);

    // Use absolute URL for local dev, or relative for production
    const API_URL = process.env.REACT_APP_API_URL || (window.location.hostname === 'localhost' ? 'http://localhost:5000/api' : '/api');

    const handleLookup = async (e) => {
        e.preventDefault();
        if (!email) return;

        setLoading(true);
        setError('');
        setProjects(null);

        try {
            const response = await fetch(`${API_URL}/v1/query`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email })
            });
            const data = await response.json();

            if (data.success) {
                setProjects(data.projects);
            } else {
                setError(data.message || 'No projects found for this email');
            }
        } catch (err) {
            setError('Failed to connect to the server');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };


    const getSteps = (currentStatus) => {
        const steps = ['Received', 'In Review', 'In Progress', 'Finalizing', 'Delivered'];
        const status = (currentStatus || 'Received').toLowerCase();

        let activeIndex = 0;
        if (status.includes('review')) activeIndex = 1;
        if (status.includes('progress')) activeIndex = 2;
        if (status.includes('finalizing') || status.includes('testing')) activeIndex = 3;
        if (status.includes('delivered') || status.includes('complete') || status.includes('finished')) activeIndex = 4;

        return { steps, activeIndex };
    };

    const getStepIcon = (step, isActive, isCompleted) => {
        const icons = {
            'Received': 'inbox',
            'In Review': 'rate_review',
            'In Progress': 'engineering',
            'Finalizing': 'task_alt',
            'Delivered': 'check_circle'
        };
        return icons[step] || 'circle';
    };

    return (
        <div className="bg-dark-bg min-h-screen text-white flex flex-col antialiased font-inter">
            <Header variant="landing" />

            <main className="flex-grow flex flex-col items-center px-6 py-20">
                <div className="max-w-4xl w-full space-y-12">
                    {/* Hero */}
                    <div className="text-center space-y-6">
                        <span className="material-symbols-outlined text-6xl text-white/80">
                            track_changes
                        </span>
                        <h1 className="text-5xl md:text-7xl font-bold tracking-tight text-white">
                            Track Your Project
                        </h1>
                        <p className="text-white/60 text-xl max-w-2xl mx-auto">
                            Enter your email to see real-time updates on your project's progress
                        </p>
                    </div>

                    {/* Search Form */}
                    <form onSubmit={handleLookup} className="relative">
                        <div className="bg-dark-card border border-white/20 rounded-2xl p-2 flex flex-col md:flex-row gap-2 backdrop-blur-sm">
                            <div className="flex-grow relative">
                                <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                                    mail
                                </span>
                                <input
                                    type="email"
                                    placeholder="your@email.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="w-full bg-transparent border-none rounded-xl pl-12 pr-6 py-4 text-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all"
                                    required
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={loading}
                                className="bg-white hover:bg-gray-200 text-black px-10 py-4 rounded-xl font-bold uppercase tracking-wider transition-all disabled:opacity-50 flex items-center justify-center gap-2 active:scale-95"
                            >
                                {loading ? (
                                    <>
                                        <span className="material-symbols-outlined animate-spin">refresh</span>
                                        Searching...
                                    </>
                                ) : (
                                    <>
                                        <span className="material-symbols-outlined">search</span>
                                        Search
                                    </>
                                )}
                            </button>
                        </div>
                    </form>

                    {/* Error Message */}
                    {error && (
                        <div className="bg-dark-card border border-red-500/30 rounded-2xl p-6 text-center">
                            <span className="material-symbols-outlined text-4xl text-red-400 mb-2">error</span>
                            <p className="text-red-400 font-medium">{error}</p>
                        </div>
                    )}

                    {/* Loading Skeleton */}
                    {loading && !error && (
                        <div className="space-y-8 animate-in fade-in duration-500">
                            {[1, 2].map((i) => (
                                <div key={i} className="relative">
                                    <div className="bg-dark-card border border-white/20 rounded-2xl p-8 md:p-10 space-y-8">
                                        {/* Header Skeleton */}
                                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 pb-8 border-b border-white/10">
                                            <div className="space-y-3 flex-1">
                                                <div className="h-8 bg-gray-700/50 rounded-lg w-48 animate-pulse"></div>
                                                <div className="flex items-center gap-3">
                                                    <div className="h-4 bg-gray-700/50 rounded w-32 animate-pulse"></div>
                                                    <div className="h-4 bg-gray-700/50 rounded w-24 animate-pulse"></div>
                                                </div>
                                            </div>
                                            <div className="h-10 bg-gray-700/50 rounded-full w-32 animate-pulse"></div>
                                        </div>

                                        {/* Progress Bar Skeleton - Desktop */}
                                        <div className="hidden md:flex justify-between gap-4 pt-4">
                                            {[1, 2, 3, 4, 5].map((step) => (
                                                <div key={step} className="flex flex-col items-center gap-4">
                                                    <div className="w-16 h-16 bg-gray-700/50 rounded-full animate-pulse"></div>
                                                    <div className="h-3 bg-gray-700/50 rounded w-20 animate-pulse"></div>
                                                </div>
                                            ))}
                                        </div>

                                        {/* Progress Bar Skeleton - Mobile */}
                                        <div className="md:hidden space-y-4">
                                            {[1, 2, 3, 4, 5].map((step) => (
                                                <div key={step} className="flex items-center gap-4">
                                                    <div className="w-12 h-12 bg-gray-700/50 rounded-full animate-pulse"></div>
                                                    <div className="h-4 bg-gray-700/50 rounded w-32 animate-pulse"></div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Enhanced Results */}
                    {projects && (
                        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-8 duration-700">
                            {projects.map((project) => {
                                const { steps, activeIndex } = getSteps(project.status);

                                return (
                                    <div key={project.id} className="relative">
                                        <div className="bg-dark-card border border-white/20 rounded-2xl p-8 md:p-10 space-y-10 hover:border-white/30 transition-all duration-300">
                                            {/* Header */}
                                            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 pb-8 border-b border-white/10">
                                                <div className="space-y-2">
                                                    <h3 className="text-3xl font-bold text-white">
                                                        {project.teamName}
                                                    </h3>
                                                    <div className="flex items-center gap-3 text-gray-400">
                                                        <span className="material-symbols-outlined text-sm">category</span>
                                                        <span className="text-sm font-medium">{project.deliverableType}</span>
                                                        <span className="text-gray-600">•</span>
                                                        <span className="material-symbols-outlined text-sm">calendar_today</span>
                                                        <span className="text-sm">{new Date(project.timestamp).toLocaleDateString()}</span>
                                                    </div>
                                                </div>
                                                <div className="bg-white text-black px-6 py-3 rounded-full text-sm font-bold uppercase tracking-widest flex items-center gap-2">
                                                    <span className="material-symbols-outlined text-lg">
                                                        {getStepIcon(steps[activeIndex], true, false)}
                                                    </span>
                                                    {project.status || 'Received'}
                                                </div>
                                            </div>

                                            {/* Enhanced Progress Bar - Desktop */}
                                            <div className="hidden md:block relative pt-4">
                                                <div className="flex justify-between relative">
                                                    {/* Background Line */}
                                                    <div className="absolute top-8 left-0 w-full h-1 bg-white/10 rounded-full"></div>

                                                    {/* Active Line */}
                                                    <div
                                                        className="absolute top-8 left-0 h-1 bg-white rounded-full transition-all duration-1000 ease-out"
                                                        style={{ width: `${(activeIndex / (steps.length - 1)) * 100}%` }}
                                                    ></div>

                                                    {steps.map((step, idx) => {
                                                        const isActive = idx === activeIndex;
                                                        const isCompleted = idx < activeIndex;

                                                        return (
                                                            <div key={step} className="flex flex-col items-center gap-4 relative z-10">
                                                                {/* Step Circle */}
                                                                <div className={`w-16 h-16 rounded-full border-4 flex items-center justify-center transition-all duration-500 ${isActive
                                                                    ? 'bg-white border-white scale-110'
                                                                    : isCompleted
                                                                        ? 'bg-white border-white'
                                                                        : 'bg-dark-card border-white/20'
                                                                    }`}>
                                                                    <span className={`material-symbols-outlined text-2xl ${isActive || isCompleted ? 'text-black' : 'text-gray-600'
                                                                        }`}>
                                                                        {getStepIcon(step, isActive, isCompleted)}
                                                                    </span>
                                                                </div>

                                                                {/* Step Label */}
                                                                <div className="text-center">
                                                                    <span className={`text-xs uppercase tracking-widest font-bold whitespace-nowrap transition-colors duration-500 ${isActive || isCompleted ? 'text-white' : 'text-gray-600'
                                                                        }`}>
                                                                        {step}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>

                                            {/* Enhanced Progress Bar - Mobile */}
                                            <div className="md:hidden space-y-2">
                                                {steps.map((step, idx) => {
                                                    const isActive = idx === activeIndex;
                                                    const isCompleted = idx < activeIndex;

                                                    return (
                                                        <div key={step} className="flex items-center gap-4">
                                                            <div className="flex flex-col items-center">
                                                                {/* Step Circle */}
                                                                <div className={`w-12 h-12 rounded-full border-3 flex items-center justify-center transition-all duration-500 ${isActive
                                                                    ? 'bg-white border-white'
                                                                    : isCompleted
                                                                        ? 'bg-white border-white'
                                                                        : 'bg-dark-card border-white/20'
                                                                    }`}>
                                                                    <span className={`material-symbols-outlined ${isActive || isCompleted ? 'text-black' : 'text-gray-600'
                                                                        }`}>
                                                                        {getStepIcon(step, isActive, isCompleted)}
                                                                    </span>
                                                                </div>

                                                                {/* Connector Line */}
                                                                {idx < steps.length - 1 && (
                                                                    <div className={`w-1 h-8 my-1 rounded-full transition-all duration-500 ${idx < activeIndex ? 'bg-white' : 'bg-white/10'
                                                                        }`}></div>
                                                                )}
                                                            </div>

                                                            {/* Step Info */}
                                                            <div className="flex-grow">
                                                                <span className={`text-sm uppercase tracking-wider font-bold transition-colors duration-500 ${isActive || isCompleted ? 'text-white' : 'text-gray-600'
                                                                    }`}>
                                                                    {step}
                                                                </span>
                                                                {isActive && (
                                                                    <p className="text-xs text-gray-400 mt-1">Current Status</p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </main>

            <Footer variant="landing" />
        </div>
    );
};

export default ProjectStatus;

